import { format } from 'date-fns'

interface IGTMParameters {
	[key: string]: any
}

export const AFFILIATION = 'CarFree'

export enum EGTMItemCategory {
	ADDON = 'addon',
	INSURANCE = 'insurance',
	ABROAD_TRAVEL = 'abroad_travel',
}

// page_type eventu page_viewed — jedna wartość na stronę, wysyłana przez usePageViewed()
export enum EGTMPageType {
	HOMEPAGE = 'homepage',
	CARS = 'cars',
	CONTACT = 'contact',
	FAQ = 'faq',
	LOCATIONS = 'locations',
	LOCATION_LANDING = 'location_landing',
	NEWS = 'news',
	NEWS_POST = 'news_post',
	ABOUT_US = 'about_us',
	LOYALTY_PROGRAM = 'loyalty_program',
	SHORT_TERM_RENTAL = 'short_term_rental',
	MEDIUM_TERM_RENTAL = 'medium_term_rental',
	LONG_TERM_RENTAL = 'long_term_rental',
	RENTAL_1_MONTH = 'rental_1_month',
	RENTAL_2_MONTHS = 'rental_2_months',
	RENTAL_3_MONTHS = 'rental_3_months',
	RENTAL_6_MONTHS = 'rental_6_months',
	WEEKEND_RENTAL = 'weekend_rental',
	HOLIDAY_RENTAL = 'holiday_rental',
	HOLIDAY_ABROAD_RENTAL = 'holiday_abroad_rental',
	BUSINESS_TRIP_RENTAL = 'business_trip_rental',
	BUS_6_RENTAL = 'bus_6_rental',
	BUS_7_RENTAL = 'bus_7_rental',
	BUS_8_RENTAL = 'bus_8_rental',
	BUS_9_RENTAL = 'bus_9_rental',
	BUS_RENTAL = 'bus_rental',
	DELIVERY_BUS_RENTAL = 'delivery_bus_rental',
	REFRIGERATION_VAN_RENTAL = 'refrigeration_van_rental',
	INSURANCE_REPLACEMENT = 'insurance_replacement',
	RENT_TESLA = 'rent_tesla',
	ELECTRIC_CAR_RENTAL = 'electric_car_rental',
	RESERVATION = 'reservation',
	RESERVATION_COMPLETED = 'reservation_completed',
	SIGN_IN = 'sign_in',
	LOGIN = 'login',
	CREATE_NEW_PASSWORD = 'create_new_password',
	VERIFY_EMAIL = 'verify_email',
	ACCOUNT = 'account',
	ACCOUNT_DATA = 'account_data',
	ACCOUNT_ORDERS = 'account_orders',
	ACCOUNT_ORDER_DETAILS = 'account_order_details',
	ACCOUNT_PASSWORD = 'account_password',
	ACCOUNT_NOTIFICATIONS = 'account_notifications',
	ACCOUNT_LANGUAGE = 'account_language',
	PRIVACY_POLICY = 'privacy_policy',
	TERMS_OF_SERVICE = 'terms_of_service',
	GENERAL_TERMS_AND_CONDITIONS = 'general_terms_and_conditions',
	DELETE_ACCOUNT = 'delete_account',
	DOCUMENTS = 'documents',
	ERROR = 'error',
	NOT_FOUND = 'not_found',
}

export const useGTMService = () => {
	if (process.server) return
	const config = useNuxtApp().$config.public
	// if (config.environment !== 'production' && config.environment !== 'staging') return
	const userStore = useUserStore()
	const route = useRoute()

	// Użytkownik klika na button “search” na głównej stronie lub na innej stronie (np. listing ofert i ponowne kliknięcie w button)
	const searchButtonClicked = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('search_button_clicked', params)
	}

	// Użytkownik otrzymał listę aut dostępnych w danej lokalizacji
	// przy każdym odświeżeniu, event odpala się na nowo np. zmiana filtrów lub jakiegoś parametru przez Usera
	const searchListingViewed = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getBookerDetails(), ...getCurrency() }
		pushDataLayer('search_listing_viewed', params)
	}

	// 	Użytkownik zmienia dolne filtry na listingu dostępnych samochodów
	// - jeśli użytkownik klika na “usuń wszystkie filtry” to mamy interaction_type “remove” oraz element_value “all”
	// - każda zmiana/dodanie/usunięcie triggeruje nowy event - zmiana kilku filtrów to kilka osobnych eventów
	// - “remove” może nastąpić poprzed odkliknięcie lub “x” na przypiętych chipsach
	const listingFiltersInteraction = (params: IGTMParameters) => {
		pushDataLayer('listing_filters_interaction', params)
	}

	// Użytkownik klika na dany samochód z listingu produktów
	const vehicleClicked = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('vehicle_clicked', params)
	}

	const vehicleViewed = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('vehicle_clicked', params)
	}

	// Użytkownik kliknął przejdź dalej lub wybrał jedno z ubezpieczeń na stronie Product Page
	const bookingStarted = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getBookerDetails(), ...getBookingData(), ...getCurrency() }
		pushDataLayer('booking_started', params)
	}

	const purchase = (params: IGTMParameters) => {
		params = { ...getBookerDetails(), ...getBookingData(), ...getCurrency(), ...params }
		pushDataLayer('purchase', params)
	}

	const insuranceSelected = (params: IGTMParameters) => {
		params = { ...params, ...getCurrency() }
		pushDataLayer('insurance_selected', params)
	}

	// Wybrane auto trafia do koszyka — wejście na stronę rezerwacji lub zmiana klasy auta przez upsell
	const addCarToCart = (params: IGTMParameters) => {
		params = { ...params, ...getBookerDetails(), ...getBookingData(), ...getCurrency(), interaction_type: params.interaction_type ?? 'regular' }
		pushDataLayer('add_to_cart', params)
	}

	// Użytkownik otworzył szczegóły ceny (podgląd zawartości koszyka)
	const viewCart = (params: IGTMParameters = {}) => {
		params = { ...params, ...getBookerDetails(), ...getBookingData(), ...getCurrency() }
		pushDataLayer('view_cart', params)
	}

	const getCartEventParams = (service: IBookingServiceModel, quantity: number) => {
		const params: IGTMParameters = { ...getBookerDetails(), ...getBookingData(), ...getCurrency() }
		if (params.ecommerce) params.ecommerce.items = [getServiceItem(service, quantity)]
		return params
	}

	const addToCart = (service: IBookingServiceModel, quantity: number = 1) => {
		pushDataLayer('add_to_cart', { ...getCartEventParams(service, quantity), interaction_type: 'regular' })
	}

	const removeFromCart = (service: IBookingServiceModel, quantity: number = 1) => {
		pushDataLayer('remove_from_cart', getCartEventParams(service, quantity))
	}

	// Użytkownik dodał jeden z upselli w ramach procesu bookingowego.
	const bookingServiceAdded = (params: IGTMParameters) => {
		params = { ...params, ...getCurrency() }
		pushDataLayer('booking_services_added', params)
	}

	// OStatni krok rezerwacji
	const checkoutStarted = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getBookerDetails(), ...getBookingData(), ...getCurrency() }
		pushDataLayer('checkout_started', params)
	}

	// Wybrana metoda płatności
	const addPaymentInfo = (params: IGTMParameters = {}) => {
		params = { ...params, ...getBookerDetails(), ...getBookingData(), ...getCurrency(), ...getPaymentAmounts() }
		params.ecommerce = { ...params.ecommerce, shipping_tier: getShippingTier(), payment_type: getPaymentType() }
		pushDataLayer('add_payment_info', params)
	}

	// Formularz rezerwacji został w całości poprawnie wypełniony
	const addBillingInfo = (params: IGTMParameters = {}) => {
		pushDataLayer('add_billing_info', params)
	}

	const addShippingInfo = (params: IGTMParameters = {}) => {
		params = { ...params, ...getBookerDetails(), ...getBookingData(), ...getCurrency(), ...getPaymentAmounts() }
		params.ecommerce = { ...params.ecommerce, shipping_tier: getShippingTier() }
		pushDataLayer('add_shipping_info', params)

		// przy tej opcji nie ma metod płatności do wyboru, więc add_payment_info nie miałby kto wysłać
		if (isNoPaymentMethodNeeded()) addPaymentInfo()
	}

	// etykieta wybranego typu płatności — ten sam tytuł, który widzi użytkownik na kafelku
	const getShippingTier = () => {
		const bookingStore = useBookingStore()
		const { $i18n } = useNuxtApp()
		const t = (key: string) => $i18n.t(`modules.reservation_module.reservation.payment.${key}`)

		if (bookingStore.getPaymentType !== EPaymentType.PAY_LATER) return t('pay_now')
		return bookingStore.getBooking?.advanceAmount === 0 ? t('pay_at_pickup') : t('pay_on_delivery')
	}

	const isNoPaymentMethodNeeded = () => {
		const bookingStore = useBookingStore()
		return bookingStore.getPaymentType === EPaymentType.PAY_LATER && bookingStore.getBooking?.advanceAmount === 0
	}

	const getPaymentType = () => {
		const method = useBookingStore().getPaymentMethod
		if (method) return getPaymentMethod(method)?.gtmLabel ?? null
		// bez metod do wyboru jedyną informacją o płatności jest typ, więc wysyłamy jego etykietę
		return isNoPaymentMethodNeeded() ? getShippingTier() : null
	}

	const getPaymentAmounts = () => {
		const booking = useBookingStore().getBooking
		const isAdvancePayment = useBookingStore().getPaymentType === EPaymentType.PAY_LATER
		const convert = (value?: number | null) => Number((value ?? 0).toFixed(2))

		return {
			overall_payment_amount: convert(booking?.total),
			advance_payment_amount: isAdvancePayment ? convert(booking?.advanceAmount) : 0,
			payment_currency: useCookie('currency').value,
		}
	}

	// standardowy ecommerce + shipping_tier i payment_type, tak jak w add_shipping_info / add_payment_info
	const getCheckoutSummaryParams = (params: IGTMParameters) => {
		const summaryParams: IGTMParameters = {
			...params,
			...getBookerDetails(),
			...getBookingData(),
			...getCurrency(),
			...getPaymentAmounts(),
		}
		summaryParams.ecommerce = { ...summaryParams.ecommerce, shipping_tier: getShippingTier(), payment_type: getPaymentType() }
		return summaryParams
	}

	// Użytkownik zaznaczył wymagane zgody na stronie rezerwacji
	const addConsents = (params: IGTMParameters = {}) => {
		pushDataLayer('add_consents', getCheckoutSummaryParams(params))
	}

	// Użytkownik kliknął potwierdzenie zamówienia na stronie rezerwacji
	const beginPayment = (params: IGTMParameters = {}) => {
		pushDataLayer('begin_payment', getCheckoutSummaryParams(params))
	}

	const userLogin = (params: IGTMParameters) => {
		pushDataLayer('user_sign_in', params)
	}

	const userRegister = (params: IGTMParameters) => {
		pushDataLayer('user_sign_up', params)
	}

	const formStarted = (params: IGTMParameters) => {
		pushDataLayer('form_started', params)
	}

	const formSubmitted = (params: IGTMParameters) => {
		pushDataLayer('form_submitted', params)
	}

	const newsletterSubmit = (params: IGTMParameters) => {
		pushDataLayer('newsletter_submit', params)
	}

	const pageViewed = (params: IGTMParameters) => {
		pushDataLayer('page_viewed', params)
	}

	const offerInterest = (params: IGTMParameters) => {
		pushDataLayer('offer_interest', params)
	}

	const pushDataLayer = (eventName: string, params?: { [key: string]: string | number }) => {
		// @ts-ignore google tag manager Name
		window.dataLayer = window.dataLayer || []

		const user = userStore.getUser
		const { $i18n } = useNuxtApp()
		const locale = $i18n.locale

		const userBasic = {
			user_id: user?.id ?? null,
			user_email: user?.email ?? null,
			user_email_hash: hashEmail(user?.email) ?? null,
			user_language: locale.value,
			custom_id: getUserCustomUUID(),
			env: config.environment,
			item_list_id: route.name,
			item_list_url: route.fullPath,
		}
		const dates = {
			event_date: formatDate(useDateTimeService().businessNow()),
		}
		// czyszczenie pól zbiorczych przed pushem eventu, żeby wartości z poprzedniego eventu nie wyciekły do następnego
		const resetParams: IGTMParameters = {}
		if (params && params.ecommerce) {
			resetParams.ecommerce = null
			if (params.car_id) resetParams.car_id = null
		}
		if (params && params.available_cars) resetParams.available_cars = null
		// @ts-ignore google tag manager Name
		if (Object.keys(resetParams).length) window.dataLayer.push(resetParams)
		// @ts-ignore google tag manager Name
		window.dataLayer.push({ event: eventName, ...userBasic, ...dates, ...params })
	}

	const generateFallbackUUID = () => {
		return 'xxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
	}

	const getUserCustomUUID = () => {
		const uuid = useCookie('cf-uuid').value

		if (!uuid) {
			const newUUID = (typeof crypto?.randomUUID === 'function' ? crypto?.randomUUID() : generateFallbackUUID())?.replace(/-/g, '').slice(0, 12)

			if (newUUID) {
				useCookie('cf-uuid').value = newUUID
			}

			return newUUID
		}

		return uuid
	}

	const pushParamsToDataLayer = (params: IGTMParameters) => {
		// @ts-ignore google tag manager Name
		window.dataLayer = window.dataLayer || []
		// @ts-ignore google tag manager Name
		window.dataLayer.push({ ...params })
	}

	const formatDate = (date: Date | null | undefined, dateformat: string = 'yyyy-MM-dd HH:mm') => {
		if (!date) return null
		return format(date, dateformat)
	}

	const getCurrency = () => {
		return { currency: useCookie('currency').value }
	}

	const getBookerData = () => {
		const bookingStore = useBookingStore()
		const deliveryCosts = useBookerStore().getDeliveryCosts
		const locations = bookingStore.getBookerLocations
		const dates = bookingStore.getBookerDates
		const startLocation = locations?.start
		const endLocation = locations?.end ? locations.end : locations?.start

		return {
			date_in: formatDate(dates?.start),
			pickup_city: startLocation?.city,
			pickup_point: getLocationPoint(startLocation),
			date_out: formatDate(dates?.end),
			return_city: endLocation?.city,
			return_point: getLocationPoint(endLocation),
			custom_pickup_address: !!startLocation?.isPickupLocation,
			custom_return_address: !!endLocation?.isPickupLocation,
			custom_pickup_price: startLocation?.isPickupLocation ? deliveryCosts?.start : null,
			custom_return_price: endLocation?.isPickupLocation ? (deliveryCosts?.end ? deliveryCosts?.end : deliveryCosts?.start) : null,
			days: bookingStore.getDays,
		}
	}

	const getLocationLabel = (location: Partial<ILocationModel> | null | undefined) => {
		if (!location) return null
		return [location.city, location.name].filter(Boolean).join(', ')
	}

	const getBookerDetails = () => {
		const bookingStore = useBookingStore()
		const locations = bookingStore.getBookerLocations
		const dates = bookingStore.getBookerDates
		const startLocation = locations?.start
		const endLocation = locations?.end

		return {
			date_start: formatDate(dates?.start, 'dd.MM.yyyy') ?? null,
			date_end: formatDate(dates?.end, 'dd.MM.yyyy') ?? null,
			hour_start: formatDate(dates?.start, 'HH:mm') ?? null,
			hour_end: formatDate(dates?.end, 'HH:mm') ?? null,
			number_of_days: bookingStore.getDays ?? null,
			location_start: getLocationLabel(startLocation),
			location_end: getLocationLabel(endLocation),
		}
	}

	const getServiceCategory = (service: IBookingServiceModel) => {
		if (isInsuranceService(service)) return EGTMItemCategory.INSURANCE
		if (isAbroadZoneService(service)) return EGTMItemCategory.ABROAD_TRAVEL
		return EGTMItemCategory.ADDON
	}

	const getServiceItem = (service: IBookingServiceModel, quantity?: number) => {
		const category = getServiceCategory(service)
		const dayPrice = service.amount ?? 0

		return {
			item_id: [category, getServiceNameLabel(service.name)].filter(Boolean).join('_'),
			item_brand: AFFILIATION,
			item_name: service.name,
			item_category: category,
			quantity: quantity ?? service.quantity ?? 1,
			price: Number(dayPrice.toFixed(2)),
			affiliation: AFFILIATION,
			...getCurrency(),
		}
	}

	const getServiceItems = (services: IBookingServiceModel[] | null | undefined) =>
		pickSelectedExtraServices(services).map((service) => getServiceItem(service))

	const getAddonItem = (service: IBookingServiceModel, dayPrice: number) => ({
		service_type_name: service.serviceTypeName,
		service_id: service.serviceID,
		service_group_id: service.serviceGroupID,
		service_name: service.name,
		day_price: Number(dayPrice.toFixed(2)),
		...getCurrency(),
		quantity: service.quantity,
		discount: Number((service.discount_total_gross ?? 0).toFixed(2)),
	})

	const getBookingData = () => {
		const bookingStore = useBookingStore()
		const booking = bookingStore.getBooking
		if (!booking) return {}
		const car = bookingStore.getSelectedCar
		const addonServices = bookingStore.getAddonServices
		const ecommerce = {
			...getCurrency(),
			value: Number((booking.total ?? 0).toFixed(2)),
			shipping: 0,
			tax: Number((booking.totalVat ?? 0).toFixed(2)),
			discount: Number((booking.totalDiscount ?? 0).toFixed(2)),
			pending_points: booking.totalPendingPoints,
			rent: Number((bookingStore.getCarRentCost ?? 0).toFixed(2)),
			addition: Number(bookingStore.getAdditionalServicesCost.toFixed(2)),
			insurance: Number((findSelectedInsurance(booking.bookingServices)?.amountTotal ?? 0).toFixed(2)),
			deposit: Number((booking.deposit ?? 0).toFixed(2)),
			coupon: booking.coupon?.code,
		}
		const items: any[] = [
			{
				item_id: car ? car.id : null,
				item_brand: car ? car.brand : null,
				item_name: getCarNameLabel(car),
				item_category: getCarSegmentLabel(car),
				segment_id: car && car.segment ? car.segment.id : null,
				service_group_id: getSelectedServiceGroupIDs(booking.bookingServices),
				quantity: 1,
				price: Number((bookingStore.getCarRentCost ?? 0).toFixed(2)),
				affiliation: AFFILIATION,
				...getCurrency(),
			},
			...getServiceItems(booking.bookingServices),
		]

		const addons = addonServices.filter((service) => service.isSelected).map((service) => getAddonItem(service, service.amount ?? 0))
		ecommerce.items = items
		return { ecommerce, addons }
	}

	const getLocationPoint = (location: ILocationModel | null) => {
		if (!location) return ''
		const { city, addressStreet } = location

		const sanitize = (value: string): string =>
			value
				.replace(/^ul\.\s*/i, '') // przedrostek "ul."
				.replace(/[\s\-–—]+/g, '_')
				.toLowerCase()
		const sanitizedCity = city ? sanitize(city) : ''
		const sanitizedStreet = addressStreet ? sanitize(addressStreet) : ''
		return [sanitizedCity, sanitizedStreet].filter(Boolean).join('_')
	}

	return {
		searchButtonClicked,
		searchListingViewed,
		listingFiltersInteraction,
		vehicleClicked,
		vehicleViewed,
		bookingStarted,
		bookingServiceAdded,
		checkoutStarted,
		addPaymentInfo,
		addShippingInfo,
		addBillingInfo,
		addConsents,
		beginPayment,
		insuranceSelected,
		addCarToCart,
		viewCart,
		addToCart,
		removeFromCart,
		getServiceItem,
		getServiceItems,
		getAddonItem,
		pushParamsToDataLayer,
		formatDate,
		getLocationPoint,
		getLocationLabel,
		userLogin,
		userRegister,
		formStarted,
		formSubmitted,
		newsletterSubmit,
		pageViewed,
		offerInterest,
		purchase,
	}
}
